import React from "react";

import cx from "classnames";

import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";

import styles from "../css/Auth.module.css";

/* eslint-disable camelcase */
const PRODUCT_SORT_ORDER = {
	FREE: 0,
	PRO: 1,
	BUSINESS: 2,
	ENTERPRISE: 3,
	ENTERPRISE_PLUS: 4,
	SOLOPRENEUR: -1,
	FEEDBACK: -1,
	OBJECTIVES: -1,
	MEETING_RECORDER: -1,
};
/* eslint-disable camelcase */

export function workspaceSort(
	{ billing_account: billingAccountA }: FellowAccount,
	{ billing_account: billingAccountB }: FellowAccount,
) {
	if (!billingAccountB) return -1;
	if (!billingAccountA) return 1;

	const { product_family: productFamilyA, active_configured_billable_user_count: numBillableUsersA } = billingAccountA;
	const { product_family: productFamilyB, active_configured_billable_user_count: numBillableUsersB } = billingAccountB;

	if (PRODUCT_SORT_ORDER[productFamilyA] > PRODUCT_SORT_ORDER[productFamilyB]) return -1;
	if (PRODUCT_SORT_ORDER[productFamilyB] > PRODUCT_SORT_ORDER[productFamilyA]) return 1;

	if (numBillableUsersA > numBillableUsersB) return -1;
	if (numBillableUsersB > numBillableUsersA) return 1;

	return 0;
}

interface WorkspacePillProps {
	account: FellowAccount;
	member?: boolean;
	guest?: boolean;
}

export function WorkspacePill({ account, member = false, guest: isGuest = false }: WorkspacePillProps) {
	const { id, name, logo, subdomain, billing_account: billingAccount } = account;
	const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";

	// eslint-disable-next-line camelcase
	const isTrialPlan = billingAccount?.subscription_state == "TRIAL";

	return (
		<>
			<Avatar
				circle
				link={false}
				user={{ id: id.toString(), fullName: name, image: logo, isGuest }}
				className={styles.authLogo}
			/>
			<div className={cx(styles.authTextWrapper, styles.authTextWorkspace)}>
				<div className={styles.authText}>
					<div className={styles.authWorkspaceName}>{name}</div>
					{billingAccount && (
						<div className={cx(styles.plan, styles[billingAccount.product_family])}>
							{billingAccount.subscription_label} {!isTrialPlan && "Plan"}
						</div>
					)}
				</div>
				<div className={styles.authSubtext}>
					{!member && billingAccount && billingAccount.active_configured_billable_user_count > 1
						? `${billingAccount.active_configured_billable_user_count} teammates in this workspace`
						: `${subdomain}${rootDomain}`}
				</div>
			</div>
		</>
	);
}

export const TOPIC_TAG_MAP = {
	agendas: "Perfect your meeting agendas",
	oneonones: "Have engaging 1-on-1 meetings",
	productive: "Supercharge your meeting productivity",
	remote: "Level-up your remote meetings",
	actionitems: "Track action items and get more done",
	feedback: "Give and get feedback",
	guests: "Run productive meetings with",
	minutes: "Record meeting minutes effectively",
	engineering: "Run efficient teams and ship faster",
	guidelines: "Transform your meeting culture",
	ai: "Automate your meeting workflows",
	oneononeebook: "Purpose-built to make every meeting and 1:1 a productive one",
};

export type SignupTopicTag = keyof typeof TOPIC_TAG_MAP;

export const APPS_EXTENSIONS_MAP = {
	generic: {
		description: <span className={styles.emphasis}>Integrate Fellow with your favorite tools</span>,
		image: "https://fellow.app/wp-content/uploads/2020/07/1-13.png",
	},
	apps: {
		description: (
			<>
				With Fellow’s desktop and mobile apps, you can untether{" "}
				<span className={styles.emphasis}>from your browser (or your desk!)</span> to have productive one-on-ones and
				team meetings anytime, anywhere.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2020/09/apps-header-image.png",
	},
	extension: {
		description: (
			<>
				With Fellow’s browser extensions, you can{" "}
				<span className={styles.emphasis}>
					access your meeting notes right inside of <strong>Google Meet</strong>
				</span>{" "}
				and get helpful details (and one-click access to the agenda!) through your Google Calendar events.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2020/11/fellow-chrome-firefox-extensions.png",
	},
	asana: {
		description: (
			<>
				Send action items generated during your Fellow meetings over to <strong>Asana</strong> so that their{" "}
				<span className={styles.emphasis}>completion status stays in-sync between both tools!</span>
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Fellow-Asana-Header.png",
	},
	bamboohr: {
		description: (
			<>
				Support your team’s Fellow experience and{" "}
				<span className={styles.emphasis}>update your workspace users and details automatically</span> by simply
				connecting with <strong>BambooHR</strong>.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Integration-Headers-2-2.png",
	},
	box: {
		description: (
			<>
				Use Fellow and <strong>Box Drive</strong> to{" "}
				<span className={styles.emphasis}>keep track of meeting-related files, notes, and resources</span> all in one
				place.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/fellow-box-header.png",
	},
	clickup: {
		description: (
			<>
				<span className={styles.emphasis}>
					Send Fellow meeting action items to <strong>ClickUp</strong> spaces
				</span>{" "}
				(and vice versa!) and become the master of your to-do list.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/fellow-clickup-header.png",
	},
	dropbox: {
		description: (
			<>
				{/* eslint-disable max-len */}
				Use <strong>Dropbox</strong> and Fellow to <span className={styles.emphasis}>create one source of truth</span>{" "}
				for meeting-related files, notes, and resources.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/fellow-dropbox-header.png",
	},
	gcal: {
		description: (
			<>
				<span className={styles.emphasis}>Boost meeting preparation, productivity, and accountability</span> with
				Fellow’s <strong>Google Calendar</strong> integration. Access your meeting notes directly from Google Calendar
				events and stay organized.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Integration-Headers-2-1.png",
	},
	gdrive: {
		description: (
			<>
				{/* eslint-disable max-len */}
				Use <strong>Google Drive</strong> and Fellow to <span className={styles.emphasis}>create a central hub</span>{" "}
				for all meeting-related files, reports, and supporting documents.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/fellow-drive-header.png",
	},
	googletasks: {
		description: (
			<>
				Stay productive and organized by{" "}
				<span className={styles.emphasis}>
					sending Fellow meeting action items to your <strong>Google Tasks</strong> lists
				</span>{" "}
				(and vice versa!)
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Fellow-googletasks-Header.png",
	},
	hubspot: {
		description: (
			<>
				Use <strong>HubSpot + Fellow</strong> to{" "}
				<span className={styles.emphasis}>
					keep your team informed on deals, important tasks, and customer handoffs
				</span>
				.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Integration-Headers-1-1.png",
	},
	icloud: {
		description: (
			<>
				Build a <span className={styles.emphasis}>single source of truth for meeting-related documents</span> by
				attaching <strong>iCloud</strong> files to your Fellow meeting notes
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Integration-Headers.png",
	},
	jira: {
		description: (
			<>
				Turn action items generated in Fellow into <strong>Jira</strong> issues so their{" "}
				<span className={styles.emphasis}>completion status stays in sync between both tools</span>.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Integration-Headers-3.png",
	},
	msteams: {
		description: (
			<>
				Boost meeting productivity with Fellow and <strong>Microsoft Teams</strong>. Collaborate on meeting agendas,
				take notes within your video calls, and send feedback requests –{" "}
				<span className={styles.emphasis}>without leaving your chat app</span>.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/fellow-msteams-header.png",
	},
	onedrive: {
		description: (
			<>
				Use <strong>Microsoft OneDrive</strong> and Fellow to{" "}
				<span className={styles.emphasis}>create a source of truth</span> for meeting-related files, supporting
				documents, and reports.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/fellow-onedrive-header.png",
	},
	slack: {
		description: (
			<>
				<span className={styles.emphasis}>
					Level-up your meeting habits with Fellow and <strong>Slack</strong>.
				</span>{" "}
				Collaborate on meeting agendas, share meeting notes, and exchange feedback – without leaving your favorite
				messaging app.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2020/10/Illo.png",
	},
	slackworkflow: {
		description: (
			<>
				<span className={styles.emphasis}>Streamline your meeting workflows</span> by creating your own Fellow + Slack
				integration with Fellow’s steps in the <strong>Slack Workflow Builder</strong>.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2020/10/Illo.png",
	},
	todoist: {
		description: (
			<>
				<span className={styles.emphasis}>Keep everything up to date</span> by sending action items from your Fellow
				meetings directly to your <strong>Todoist</strong> lists (and vice versa!)
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Fellow-Todoist-final.png",
	},
	trello: {
		description: (
			<>
				Send action items generated during your Fellow meetings over to <strong>Trello</strong> (and vice-versa!) so
				that their <span className={styles.emphasis}>status stays updated on both tools</span>!
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Integration-Headers-1.png",
	},
	workday: {
		description: (
			<>
				Make your work as a Fellow admin easy and enhance your team’s experience by connecting Fellow and{" "}
				<strong>Workday</strong>. <span className={styles.emphasis}>It’s the best of both worlds</span>.
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2021/06/Integration-Headers-1-2.png",
	},
	zapier: {
		description: (
			<>
				Connect Fellow with <strong>Zapier</strong> to{" "}
				<span className={styles.emphasis}>automate your meeting workflows across 3,000+ apps</span>, sync information
				between tools, and fly through your meeting tasks!
			</>
		),
		image: "https://fellow.app/wp-content/uploads/2020/12/Illo.png",
	},
};

export type SignupIntegrationTag = keyof typeof APPS_EXTENSIONS_MAP;

export function BillingChip({ billingAccount }: { billingAccount: FellowAccount["billing_account"] }) {
	if (!billingAccount) return null;

	return (
		<div className={cx(styles.plan, styles[billingAccount.product_family])}>
			{billingAccount.subscription_label}
			<span className={styles.webOnly}> Plan</span>
		</div>
	);
}

type CoreFellowAccount = Pick<FellowAccount, "id" | "name" | "logo">;

export function WorkspaceRow(
	props:
		| {
				account: CoreFellowAccount & Pick<FellowAccount, "billing_account" | "teammates" | "teammates_count">;
				accountType: "member" | "guest" | "domain";
				loading: boolean;
				pickWorkspace: () => void;
				subdomain: string;
		  }
		| {
				account: CoreFellowAccount & Partial<Pick<FellowAccount, "billing_account" | "teammates" | "teammates_count">>;
				accountType: "member" | "guest" | "domain";
				loginUrl: string;
				subdomain: string;
		  },
) {
	const handlePickWorkspace = React.useCallback(() => {
		if ("pickWorkspace" in props) {
			props.pickWorkspace?.();
		}
	}, [props]);

	const { account, subdomain, accountType } = props;

	return (
		<div key={account.id} className={styles.workspaceRow}>
			<img src={account.logo} alt={account.name} />
			<div className={styles.workspaceRowContent}>
				<div>
					<div className={styles.workspaceRowTitle}>{account.name}</div>
					<div className={styles.webOnly}>
						<BillingChip billingAccount={account.billing_account} />
					</div>
				</div>
				{account.teammates && (
					<div>
						<div className={styles.workspaceRowAvatarList}>
							{account.teammates
								?.slice(0, 5)
								.map(teammate => (
									<img
										style={{ width: "16px", height: "16px", borderRadius: "50%" }}
										key={teammate.id}
										src={teammate.image}
										alt={teammate.name}
									/>
								))}
						</div>
						<div className={styles.mobileOnly}>
							<BillingChip billingAccount={account.billing_account} />
						</div>
						<div className={styles.workspaceRowTeammatesCount}>
							{account.teammates_count ?? account.teammates?.length} teammate
							{(account.teammates_count ?? account.teammates?.length) > 1 ? "s" : ""}
						</div>
					</div>
				)}
				{/* subdomain is only used in non-prod environments as an easy way for QA to identify different workspaces */}
				{window.ENVIRONMENT !== "prod" && <div>{subdomain}</div>}
			</div>
			<div className={styles.workspaceRowButtonWrapper}>
				{"loginUrl" in props ? (
					<a className={styles.workspaceRowButton} href={props.loginUrl}>
						{accountType == "domain" ? "Join team" : "Log in"}
					</a>
				) : (
					<>
						<Button
							className={styles.workspaceRowButton}
							disabled={props.loading}
							onClick={handlePickWorkspace}
							variant="primary"
						>
							{accountType == "domain" ? "Join team" : "Open"}
						</Button>
						{accountType !== "domain" && <div className={styles.workspaceRowAlreadyMember}>Already a member</div>}
					</>
				)}
			</div>
		</div>
	);
}
